import { createClient } from "@vercel/edge-config";
import { geolocation, ipAddress, waitUntil } from "@vercel/functions";
import type { NextRequest } from "next/server";
import Statsig from "statsig-node-lite";

import { ensureEnvVariable } from "@brexhq/next/src/utils/dataFetching/env";

import { timeoutWithDefaultValue } from "../timeout/timeout";

import { StatsigEdgeConfigDataAdapter } from "./StatsigEdgeConfigDataAdapter";
import type { StatsigArgs } from "./createMiddlewareConfig";

let statsigInitialization: Promise<void> | null = null;

const initializeStatsig = async () => {
  const statsigKey = ensureEnvVariable("STATSIG_SERVER_API_KEY");
  const statsigEdgeKey = ensureEnvVariable("STATSIG_EDGE_CONFIG_KEY");
  const statsigEdgeUrl = ensureEnvVariable("STATSIG_EDGE_CONFIG_URL");
  // this env is set to staging/production/development
  const environment = ensureEnvVariable("BREX_ENV");

  // initialize statsig
  const edgeConfigClient = createClient(statsigEdgeUrl);
  const dataAdapter = new StatsigEdgeConfigDataAdapter({
    edgeConfigClient: edgeConfigClient,
    edgeConfigItemKey: statsigEdgeKey,
  });

  await Statsig.initialize(statsigKey, {
    dataAdapter,
    // Local Mode is not needed to use results from edge config.
    // This is already handled by us setting the DataAdapter.
    // Setting local mode will result in exposure events not being sent.
    localMode: false,
    environment: { tier: environment ?? "development" },
  });
};

export const getStaticPropsFeatureGateValueFromStatsig = async (
  gateName: string,
): Promise<boolean> => {
  statsigInitialization ??= initializeStatsig();
  await statsigInitialization;

  const gate = Statsig.getFeatureGateSync(
    { userID: "website-anonymous-users" },
    gateName,
  );

  // flushes all events from the sdk to statsig
  // @see https://docs.statsig.com/integrations/vercel/#4-flushing-events
  waitUntil(Statsig.flush());

  return gate?.value ?? false;
};

export const getStaticPropsDynamicConfigValueFromStatsig = async <
  T = Record<string, any> | null,
>(
  configName: string,
): Promise<T | null> => {
  statsigInitialization ??= initializeStatsig();
  await statsigInitialization;

  const config = Statsig.getConfigSync(
    { userID: "website-anonymous-users" },
    configName,
  );

  // flushes all events from the sdk to statsig
  // @see https://docs.statsig.com/integrations/vercel/#4-flushing-events
  waitUntil(Statsig.flush());

  return config?.value as T | null;
};

export const getFeatureGateValueFromStatsig = async (
  anonymousID: string,
  request: NextRequest,
  gateName: string,
): Promise<boolean> => {
  statsigInitialization ??= initializeStatsig();
  await statsigInitialization;

  const country = geolocation(request).country;
  const ip = ipAddress(request);

  const gate = Statsig.getFeatureGateSync(
    {
      customIDs: { anonymousID },
      country,
      ip,
    },
    gateName,
  );

  // flushes all events from the sdk to statsig
  // @see https://docs.statsig.com/integrations/vercel/#4-flushing-events
  waitUntil(Statsig.flush());

  return gate?.value ?? false;
};

export const getExperimentValueFromStatsig = async (
  anonymousID: string,
  request: NextRequest,
  experimentData: StatsigArgs,
) => {
  statsigInitialization ??= initializeStatsig();
  await statsigInitialization;

  const country = geolocation(request).country;
  const ip = ipAddress(request);

  const experiment = Statsig.getExperimentSync(
    // ip and geo will be undefined in local development
    {
      customIDs: { anonymousID },
      country,
      ip,
    },
    experimentData.statsigExperimentId,
  );

  // flushes all events from the sdk to statsig
  // @see https://docs.statsig.com/integrations/vercel/#4-flushing-events
  waitUntil(Statsig.flush());

  const value = experiment.get<string>(
    experimentData.parameterKey,
    experimentData.defaultParameterValue,
  );

  return { value, timedOut: false };
};

/**
 * Helper function for calling Statsig from the clientside, handles timout and returning default value in case of an arror
 * @param experimentData: ExperimentData
 * @param timeoutMs
 * @returns Promise<{value: string, timedOut: boolean}>
 */
export const statsigClientSide = async (
  experimentData: StatsigArgs,
  timeoutMs = 750,
) => {
  const fetchSS = async () => {
    try {
      const expResult = await fetch("/api/ss", {
        method: "POST",
        body: JSON.stringify(experimentData),
      });
      return await expResult.json();
    } catch (e) {
      return { value: experimentData.defaultParameterValue, timedOut: true };
    }
  };

  return await timeoutWithDefaultValue<{
    value: string;
    timedOut: boolean;
  }>(
    fetchSS(),
    { value: experimentData.defaultParameterValue, timedOut: true },
    timeoutMs,
  );
};

/**
 * Helper function for calling Statsig Feature Gates from the clientside, handles timeout and returning default value in case of an error
 * @param gateName: string
 * @param timeoutMs
 * @returns Promise<boolean>
 */
export const getFeatureGateClientSide = async (
  gateName: string,
  timeoutMs = 750,
): Promise<boolean> => {
  const fetchGate = async (): Promise<boolean> => {
    try {
      const gateResult = await fetch("/api/ss-ff", {
        method: "POST",
        body: JSON.stringify({ gateName }),
      });
      // The API returns { value: boolean }
      const result = await gateResult.json();
      return result.value; // Directly return the boolean value
    } catch (e) {
      console.error("Error fetching feature gate:", gateName, e); // Log gateName for better debugging
      return false; // Default to false on error
    }
  };

  // Assuming timeoutWithDefaultValue can handle a direct boolean and returns a boolean
  return await timeoutWithDefaultValue<boolean>(
    fetchGate(),
    false, // Default value is false
    timeoutMs,
  );
};
